import { useState, useEffect } from 'react';
  const perms = detectPerms();
import { 
  Plus, 
  Pencil, 
  Trash2, 
  X, 
  Users, 
  Search,
  Check,
  AlertCircle,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import { detectPerms } from '../../permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const initialFormData = {
  name: '',
  email: '',
  phone: '',
  gst_number: '',
  shipping_address: '',
  billing_address: '',
  active: true
};

export const Vendors = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/vendors`, getAuthHeaders());
      setVendors(response.data);
    } catch (err) {
      console.error('Error fetching vendors:', err);
      setError('Failed to fetch vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const openAddModal = () => {
    setFormData(initialFormData);
    setEditingVendor(null);
    setError('');
    setShowModal(true);
  };

  const openEditModal = (vendor) => {
    setFormData({
      name: vendor.name || '',
      email: vendor.email || '',
      phone: vendor.phone || '',
      gst_number: vendor.gst_number || '',
      shipping_address: vendor.shipping_address || '',
      billing_address: vendor.billing_address || '',
      active: vendor.active ?? true
    });
    setEditingVendor(vendor);
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.name.trim()) {
      setError('Vendor name is required');
      return;
    }

    setSubmitting(true);
    try {
      const payload = { ...formData };
      ['email', 'phone', 'gst_number', 'shipping_address', 'billing_address'].forEach(k => {
        if (!payload[k]?.trim()) payload[k] = null;
      });
      if (editingVendor) {
        await axios.put(
          `${BACKEND_URL}/api/vendors/${editingVendor.id}`,
          payload,
          getAuthHeaders()
        );
      } else {
        await axios.post(
          `${BACKEND_URL}/api/vendors`,
          payload,
          getAuthHeaders()
        );
      }
      setShowModal(false);
      fetchVendors();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save vendor');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (vendorId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/vendors/${vendorId}`, getAuthHeaders());
      setDeleteConfirm(null);
      fetchVendors();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete vendor');
    }
  };

  const filteredVendors = vendors.filter(v =>
    v.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.phone?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.gst_number?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div data-testid="vendors-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Vendors</h1>
          <p className="text-slate-600 mt-1">Manage your vendors/suppliers</p>
        </div>
        {perms.canAdd && (
        <button
          onClick={openAddModal}
          data-testid="add-vendor-btn"
          className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Add Vendor
        </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search vendors..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-input"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none transition-colors"
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Vendors Table */}
      <div className="bg-white rounded-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        ) : filteredVendors.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <Users className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No vendors yet</h3>
            <p className="text-slate-500 mb-6">Start by adding your first vendor</p>
            <button
              onClick={openAddModal}
              data-testid="empty-add-btn"
              className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors"
            >
              <Plus className="h-5 w-5" />
              Add First Vendor
            </button>
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div className="hidden md:grid grid-cols-6 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider col-span-2">Name</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Email</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Phone</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Status</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Actions</div>
            </div>

            {/* Table Rows */}
            {filteredVendors.map((vendor) => (
              <div
                key={vendor.id}
                data-testid={`vendor-row-${vendor.id}`}
                className="grid grid-cols-1 md:grid-cols-6 gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 items-center"
              >
                <div className="col-span-2">
                  <div className="font-medium text-slate-900">{vendor.name}</div>
                  {vendor.gst_number && (
                    <div className="text-xs text-slate-500 font-mono">GST: {vendor.gst_number}</div>
                  )}
                </div>
                <div className="text-slate-600 text-sm truncate">{vendor.email || '-'}</div>
                <div className="text-slate-600 text-sm">{vendor.phone || '-'}</div>
                <div>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                    vendor.active 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {vendor.active ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                    {vendor.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {perms.canEdit && (
                  <button
                    onClick={() => openEditModal(vendor)}
                    data-testid={`edit-btn-${vendor.id}`}
                    className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors"
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  )}
                  <button
                    onClick={() => setDeleteConfirm(vendor.id)}
                    data-testid={`delete-btn-${vendor.id}`}
                    className="p-2 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Confirm Delete</h3>
            <p className="text-slate-600 mb-6">Are you sure you want to delete this vendor? This action cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                data-testid="confirm-delete-btn"
                className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-lg my-8">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">
                {editingVendor ? 'Edit Vendor' : 'Add New Vendor'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6">
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
                  <AlertCircle className="h-5 w-5" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    data-testid="input-name"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                    required
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    data-testid="input-email"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    data-testid="input-phone"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                  />
                </div>

                {/* GST Number */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">GST Number</label>
                  <input
                    type="text"
                    name="gst_number"
                    value={formData.gst_number}
                    onChange={handleInputChange}
                    data-testid="input-gst"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                  />
                </div>

                {/* Billing Address */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Billing Address</label>
                  <textarea
                    name="billing_address"
                    value={formData.billing_address}
                    onChange={handleInputChange}
                    rows={2}
                    placeholder="Billing address"
                    data-testid="input-billing-address"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none resize-none"
                  />
                </div>

                {/* Shipping Address */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Shipping Address</label>
                  <textarea
                    name="shipping_address"
                    value={formData.shipping_address}
                    onChange={handleInputChange}
                    rows={2}
                    placeholder="Shipping address"
                    data-testid="input-shipping-address"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none resize-none"
                  />
                </div>

                {/* Active Status */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="active"
                    name="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                    data-testid="input-active"
                    className="w-5 h-5 text-orange-500 border-slate-300 rounded focus:ring-orange-500"
                  />
                  <label htmlFor="active" className="text-sm font-medium text-slate-700">
                    Active
                  </label>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex gap-3 justify-end pt-6 mt-6 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-btn"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editingVendor ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
